// Hierarchical category structure similar to Sahibinden
// This defines the navigation tree for sidebar browsing
import { ANIMAL_CATEGORY_GROUPS } from "./constants";

export interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
  subcategories?: string[]; // Final level items that match the flat subcategory list
}

export interface CategoryHierarchy {
  [key: string]: CategoryNode;
}

// العقارات - Real Estate
export const REAL_ESTATE_HIERARCHY: CategoryNode = {
  id: "emlak",
  name: "العقارات",
  children: [
    {
      id: "residential",
      name: "سكني",
      children: [
        {
          id: "residential-sale",
          name: "للبيع",
          subcategories: [
            "شقة للبيع",
            "فيلا للبيع",
            "منزل مستقل للبيع",
            "بيت ريفي للبيع",
            "مزرعة للبيع"
          ]
        },
        {
          id: "residential-rent",
          name: "للإيجار",
          subcategories: [
            "شقة للإيجار",
            "فيلا للإيجار",
            "منزل مستقل للإيجار",
            "إيجار يومي"
          ]
        }
      ]
    },
    {
      id: "commercial",
      name: "تجاري",
      children: [
        {
          id: "commercial-sale",
          name: "للبيع",
          subcategories: [
            "محل للبيع",
            "متجر للبيع",
            "مكتب للبيع",
            "طابق بلازا للبيع",
            "مصنع للبيع",
            "ورشة للبيع",
            "مستودع للبيع",
            "مطعم للبيع",
            "مقهى للبيع",
            "فندق للبيع",
            "شقق فندقية للبيع"
          ]
        },
        {
          id: "commercial-rent",
          name: "للإيجار",
          subcategories: [
            "محل للإيجار",
            "متجر للإيجار",
            "مكتب للإيجار",
            "طابق بلازا للإيجار",
            "مصنع للإيجار",
            "ورشة للإيجار",
            "مستودع للإيجار",
            "مطعم للإيجار"
          ]
        }
      ]
    },
    {
      id: "land",
      name: "أراضي",
      subcategories: [
        "أرض سكنية للبيع",
        "أرض زراعية للبيع",
        "أرض تجارية للبيع",
        "حقل للبيع",
        "بستان للبيع",
        "أرض للإيجار",
        "أرض زراعية للإيجار"
      ]
    },
    {
      id: "other-real-estate",
      name: "أخرى",
      subcategories: [
        "مشاريع سكنية",
        "منشأة سياحية",
        "مبنى"
      ]
    }
  ]
};

// السيارات - Vehicles
export const VEHICLES_HIERARCHY: CategoryNode = {
  id: "vasita",
  name: "السيارات",
  children: [
    {
      id: "cars",
      name: "سيارات صالون",
      // Consolidated body-type list per Yasir's call (2026-06-01):
      //   - "سيارة صالون" + "سيدان" merged into "سيدان"
      //   - "هاتشباك" + "ستيشن واجن" merged into one option
      //   - "كوبيه" carries a one-line Arabic clarification
      //   - "كابريو" → the standard Arabic word "مكشوفة"
      subcategories: [
        "سيدان",
        "هاتشباك / ستيشن واجن",
        "كوبيه (سيارة رياضية ببابَين)",
        "مكشوفة"
      ]
    },
    {
      id: "suv",
      name: "دفع رباعي/SUV",
      // "كروس أوفر" merged into the SUV row since the average Iraqi
      // buyer treats them as the same family.
      subcategories: [
        "SUV / دفع رباعي / كروس أوفر",
        "بيك أب"
      ]
    },
    {
      id: "electric",
      name: "سيارات كهربائية",
      subcategories: [
        "سيارات كهربائية",
        "سيارات هجينة"
      ]
    },
    {
      id: "motorcycles",
      name: "دراجات نارية",
      subcategories: [
        "دراجة نارية",
        "سكوتر",
        "ATV",
        "دراجة كهربائية"
      ]
    },
    {
      id: "commercial-vehicles",
      name: "مركبات تجارية",
      subcategories: [
        "فان",
        "ميني باص",
        "باص",
        "شاحنة",
        "شاحنة صغيرة",
        "مقطورة"
      ]
    },
    {
      id: "other-vehicles",
      name: "أخرى",
      children: [
        {
          id: "special-cars",
          name: "سيارات خاصة",
          subcategories: [
            "سيارات كلاسيكية",
            "سيارات متضررة",
            "سيارات للإيجار"
          ]
        },
        {
          id: "marine",
          name: "بحري",
          subcategories: [
            "قوارب",
            "يخت",
            "جت سكي",
            "شراعي"
          ]
        },
        {
          id: "rv",
          name: "كرفانات",
          subcategories: [
            "كرفان",
            "موتور هوم"
          ]
        },
        {
          id: "aviation",
          name: "طيران",
          subcategories: [
            "طائرات",
            "هليكوبتر"
          ]
        }
      ]
    }
  ]
};

// قطع الغيار - Spare Parts
export const SPARE_PARTS_HIERARCHY: CategoryNode = {
  id: "yedek-parca",
  name: "قطع غيار واكسسوارات السيارات",
  children: [
    {
      id: "car-parts",
      name: "قطع السيارات",
      subcategories: [
        "قطع المحرك", "قطع ناقل الحركة", "نظام الفرامل", "نظام التعليق",
        "نظام العادم", "نظام التبريد", "نظام الوقود", "الأجزاء الكهربائية",
        "قطع الهيكل", "المرايا", "المصابيح", "الصدامات"
      ]
    },
    {
      id: "tires-rims",
      name: "إطارات وجنوط",
      subcategories: [
        "إطارات", "جنوط", "حساس ضغط الإطارات"
      ]
    },
    {
      id: "car-accessories",
      name: "إكسسوارات السيارة",
      subcategories: [
        "إكسسوارات السيارة", "أغطية المقاعد", "سجاد السيارة", "حوض الصندوق",
        "معطر السيارة", "حامل الهاتف"
      ]
    },
    {
      id: "car-electronics",
      name: "إلكترونيات السيارة",
      subcategories: [
        "أنظمة الصوت", "راديو", "مكبرات الصوت", "سب ووفر",
        "نظام الملاحة", "كاميرا السيارة", "حساس الركن", "كاميرا خلفية"
      ]
    },
    {
      id: "motorcycle-parts",
      name: "قطع الدراجات",
      subcategories: [
        "قطع غيار الدراجات", "إكسسوارات الدراجات", "خوذة", "جاكيت",
        "قفازات", "أحذية", "معدات الحماية"
      ]
    },
    {
      id: "maintenance-products",
      name: "منتجات الصيانة",
      subcategories: [
        "زيت المحرك", "سائل التبريد", "زيت الفرامل", "سائل المساحات",
        "شامبو السيارة", "ملمع", "طقم صيانة"
      ]
    }
  ]
};

// التسوق - Shopping
export const SHOPPING_HIERARCHY: CategoryNode = {
  id: "alisveris",
  name: "التسوق",
  children: [
    {
      id: "computers",
      name: "الكمبيوتر والتابلت",
      children: [
        {
          id: "computers-devices",
          name: "أجهزة",
          subcategories: [
            "كمبيوتر", "لابتوب", "كمبيوتر مكتبي", "كمبيوتر ألعاب",
            "تابلت", "آيباد", "شاشة", "طابعة"
          ]
        },
        {
          id: "computer-parts",
          name: "قطع الكمبيوتر",
          subcategories: [
            "قطع الكمبيوتر", "معالج", "كرت شاشة", "لوحة أم", "رام", "SSD", "هارد ديسك"
          ]
        }
      ]
    },
    {
      id: "phones",
      name: "الهواتف",
      subcategories: [
        "هاتف محمول", "آيفون", "سامسونج", "شاومي", "هواوي", "أوبو",
        "إكسسوارات الهاتف", "كفر", "شاحن", "سماعات"
      ]
    },
    {
      id: "electronics",
      name: "الإلكترونيات",
      children: [
        {
          id: "tv-audio",
          name: "تلفزيون وصوتيات",
          subcategories: [
            "تلفزيون", "سمارت تي في", "LED TV", "OLED TV",
            "أنظمة الصوت", "ساوند بار", "سماعات", "سماعات رأس"
          ]
        },
        {
          id: "cameras",
          name: "كاميرات",
          subcategories: [
            "كاميرا", "كاميرا احترافية", "عدسات",
            "كاميرا فيديو", "درون", "كاميرا أكشن"
          ]
        },
        {
          id: "gaming",
          name: "ألعاب",
          subcategories: [
            "بلايستيشن", "إكس بوكس", "نينتندو"
          ]
        }
      ]
    },
    {
      id: "home-appliances",
      name: "الأجهزة المنزلية",
      children: [
        {
          id: "large-appliances",
          name: "أجهزة كبيرة",
          subcategories: [
            "ثلاجة", "غسالة ملابس", "غسالة صحون", "مجففة",
            "فرن", "طباخ", "شفاط", "مايكروويف", "فريزر",
            "مكيف", "مكنسة كهربائية", "مكنسة روبوت"
          ]
        },
        {
          id: "small-appliances",
          name: "أجهزة صغيرة",
          subcategories: [
            "ماكينة قهوة", "غلاية شاي", "توستر", "خلاط",
            "مكواة", "مجفف شعر", "محمصة خبز", "كاتل"
          ]
        }
      ]
    },
    {
      id: "clothing",
      name: "الملابس",
      children: [
        {
          id: "women-clothing",
          name: "ملابس نسائية",
          subcategories: [
            "ملابس نسائية", "فساتين", "بلوزات", "قمصان", "تيشرتات",
            "بناطيل", "تنانير", "شورتات", "جواكت", "معاطف",
            "أحذية نسائية", "كعب عالي", "أحذية رياضية", "بوت", "صنادل",
            "حقائب نسائية", "شنط يد", "شنط كتف"
          ]
        },
        {
          id: "men-clothing",
          name: "ملابس رجالية",
          subcategories: [
            "ملابس رجالية", "قمصان رجالية", "تيشرتات رجالية", "بناطيل رجالية",
            "بدلات", "جواكت رجالية", "معاطف رجالية",
            "أحذية رجالية", "أحذية كلاسيك", "أحذية رياضية رجالية"
          ]
        },
        {
          id: "kids-clothing",
          name: "ملابس أطفال",
          subcategories: [
            "ملابس أطفال", "ملابس رضع", "أحذية أطفال"
          ]
        }
      ]
    },
    {
      id: "accessories",
      name: "إكسسوارات",
      subcategories: [
        "ساعات", "ساعات جدارية", "نظارات", "نظارات شمسية", "أحزمة", "محافظ", "أوشحة"
      ]
    },
    {
      id: "furniture",
      name: "المنزل والأثاث",
      children: [
        {
          id: "living-room",
          name: "غرفة المعيشة",
          subcategories: [
            "أثاث", "طقم كنب", "كنب", "طاولة", "كرسي", "طاولة تلفزيون"
          ]
        },
        {
          id: "dining-room",
          name: "غرفة الطعام",
          subcategories: [
            "غرفة طعام"
          ]
        },
        {
          id: "bedroom",
          name: "غرفة النوم",
          subcategories: [
            "غرفة نوم", "سرير", "قاعدة سرير", "مرتبة", "مفارش", "دولاب", "مكتبة"
          ]
        },
        {
          id: "decor",
          name: "ديكور وإضاءة",
          subcategories: [
            "إضاءة", "ثريا", "لمبات", "أباجورة", "سجاد", "ستائر", "ديكور"
          ]
        },
        {
          id: "kitchen-items",
          name: "أدوات المطبخ",
          subcategories: [
            "أدوات مطبخ", "قدور", "مقالي", "صحون", "أكواب"
          ]
        }
      ]
    },
    {
      id: "garden",
      name: "الحديقة",
      subcategories: [
        "أثاث حديقة", "أدوات الحديقة", "ماكينة قص العشب"
      ]
    },
    {
      id: "sports",
      name: "الرياضة",
      subcategories: [
        "معدات رياضية", "أجهزة لياقة", "مشاية", "دراجة ثابتة",
        "دراجة هوائية", "دراجة جبلية", "دراجة كهربائية",
        "معدات تخييم", "خيمة", "كيس نوم"
      ]
    },
    {
      id: "hobbies",
      name: "الهوايات",
      subcategories: [
        "آلات موسيقية", "جيتار", "بيانو", "أورج", "كمان", "درامز",
        "تحف", "أنتيكات", "كتب", "ألعاب فيديو"
      ]
    },
    {
      id: "baby-mother",
      name: "الأم والطفل",
      subcategories: [
        "عربة أطفال", "سرير أطفال", "كرسي طعام", "كرسي سيارة",
        "مستلزمات الأطفال", "حفاضات", "رضاعات", "ألعاب"
      ]
    },
    {
      id: "beauty",
      name: "مستحضرات التجميل",
      subcategories: [
        "مستحضرات تجميل", "عطور", "مكياج", "العناية بالبشرة", "العناية بالشعر"
      ]
    }
  ]
};

// الأغذية - Food & Beverages
export const FOOD_HIERARCHY: CategoryNode = {
  id: "aghziya",
  name: "الأغذية",
  children: [
    {
      id: "grains-legumes",
      name: "الحبوب والبقوليات",
      subcategories: [
        "أرز", "قمح", "شعير", "عدس", "فاصولياء", "حمص", "فول", "ماش", "بازلاء"
      ]
    },
    {
      id: "oils-fats",
      name: "الزيوت والدهون",
      subcategories: [
        "زيت زيتون", "زيت نباتي", "زيت عباد الشمس", "زيت ذرة", "سمن", "زبدة"
      ]
    },
    {
      id: "spices",
      name: "التوابل والبهارات",
      subcategories: [
        "توابل", "بهارات", "كركم", "فلفل أسود", "كمون", "زعفران", "قرفة", "هيل", "زنجبيل"
      ]
    },
    {
      id: "canned-preserved",
      name: "المعلبات والأغذية المحفوظة",
      subcategories: [
        "معلبات", "طماطم معلبة", "تونة", "سردين", "ذرة معلبة", "فول معلب", "مخللات", "زيتون"
      ]
    },
    {
      id: "beverages",
      name: "المشروبات",
      subcategories: [
        "شاي", "قهوة", "قهوة تركية", "قهوة عربية", "نسكافيه", "عصائر", "مشروبات غازية", "مياه معدنية"
      ]
    },
    {
      id: "dairy-cheese",
      name: "الألبان والأجبان",
      subcategories: [
        "حليب", "لبن", "جبن", "قشطة", "زبادي", "حليب بودرة"
      ]
    },
    {
      id: "sweets-candy",
      name: "الحلويات والسكاكر",
      subcategories: [
        "حلويات", "شوكولاتة", "بسكويت", "كيك", "حلويات شرقية", "بقلاوة", "معمول", "تمر"
      ]
    },
    {
      id: "nuts-dried-fruits",
      name: "المكسرات والفواكه المجففة",
      subcategories: [
        "مكسرات", "فستق", "لوز", "جوز", "كاجو", "بندق", "زبيب", "تين مجفف", "مشمش مجفف"
      ]
    },
    {
      id: "flour-pastry",
      name: "الدقيق والمعجنات",
      subcategories: [
        "دقيق", "طحين", "نشا", "خميرة", "معجنات", "خبز"
      ]
    },
    {
      id: "sauces-condiments",
      name: "الصلصات والمواد الغذائية",
      subcategories: [
        "صلصة طماطم", "كاتشب", "مايونيز", "خردل", "صويا صوص", "خل"
      ]
    },
    {
      id: "honey-jams",
      name: "العسل والمربيات",
      subcategories: [
        "عسل", "عسل طبيعي", "مربى", "دبس تمر", "دبس رمان", "طحينة"
      ]
    },
    {
      id: "frozen-meats",
      name: "اللحوم والدواجن",
      subcategories: [
        "لحوم مجمدة", "دجاج مجمد", "نقانق", "برغر", "لحوم مصنعة", "أسماك مجمدة"
      ]
    },
    {
      id: "international-food",
      name: "الأغذية العالمية",
      subcategories: [
        "أغذية تركية", "أغذية صينية", "أغذية هندية", "أغذية إيطالية", "أغذية كورية", "أغذية يابانية", "أغذية مكسيكية", "أغذية تايلندية"
      ]
    },
    {
      id: "healthy-food",
      name: "أغذية صحية ومكملات",
      subcategories: [
        "أغذية عضوية", "أغذية صحية", "أغذية خالية من الغلوتين", "مكملات غذائية", "بروتين", "شوفان"
      ]
    },
    {
      id: "baby-food",
      name: "أغذية الأطفال",
      subcategories: [
        "حليب أطفال", "سيريلاك", "أغذية أطفال جاهزة"
      ]
    },
    {
      id: "restaurant-supplies",
      name: "مستلزمات المطاعم والمقاهي",
      subcategories: [
        "مستلزمات مطاعم", "أكواب ورقية", "علب تغليف طعام", "أدوات بلاستيكية", "مناديل", "معدات مطاعم"
      ]
    }
  ]
};

// الرياضة - Sports
export const SPORTS_HIERARCHY: CategoryNode = {
  id: "riyada",
  name: "الرياضة",
  children: [
    {
      id: "sports-shoes",
      name: "أحذية",
      subcategories: [
        "أحذية رياضية", "أحذية كرة قدم", "أحذية جري", "أحذية كرة سلة"
      ]
    },
    {
      id: "sports-clothing",
      name: "ملابس",
      subcategories: [
        "ملابس رياضية", "ملابس تدريب", "ملابس سباحة", "ملابس رياضية نسائية", "ملابس رياضية رجالية", "ملابس رياضية أطفال"
      ]
    },
    {
      id: "sports-bags",
      name: "حقائب",
      subcategories: [
        "حقائب رياضية", "حقائب ظهر رياضية", "حقائب سفر رياضية"
      ]
    },
    {
      id: "sports-accessories",
      name: "إكسسوارات",
      subcategories: [
        "إكسسوارات رياضية", "ساعات رياضية", "نظارات رياضية", "قفازات رياضية", "أحزمة رياضية"
      ]
    },
    {
      id: "sports-gym",
      name: "معدات كمال الأجسام",
      subcategories: [
        "معدات كمال الأجسام", "أوزان حديدية", "بار حديد", "كيتل بل", "أغذية وبروتين"
      ]
    },
    {
      id: "sports-equipment",
      name: "معدات رياضية",
      subcategories: [
        "معدات كرة القدم", "كرة قدم", "شبك مرمى",
        "معدات كرة السلة", "معدات تنس", "معدات سباحة"
      ]
    },
    {
      id: "sports-cycling",
      name: "دراجات",
      subcategories: [
        "دراجات هوائية رياضية", "إكسسوارات دراجات"
      ]
    },
    {
      id: "sports-other",
      name: "أخرى",
      subcategories: [
        "أخرى"
      ]
    }
  ]
};

// المعدات الثقيلة - Industrial Equipment
export const INDUSTRIAL_HIERARCHY: CategoryNode = {
  id: "is-makineleri",
  name: "المعدات الثقيلة",
  children: [
    {
      id: "construction-equipment",
      name: "معدات البناء",
      subcategories: [
        "حفارة", "حفارة صغيرة", "حفارة جنزير", "حفارة عجلات",
        "لودر", "بلدوزر", "جريدر", "رصاصة", "راصف أسفلت",
        "مضخة خرسانة", "خلاطة خرسانة", "محطة خرسانة", "رافعة برجية", "رافعة متنقلة"
      ]
    },
    {
      id: "loading-transport",
      name: "التحميل والنقل",
      subcategories: [
        "فورك ليفت", "فورك ليفت كهربائي", "فورك ليفت ديزل", "ترانسبالت",
        "رافعة بضائع", "منصة رفع"
      ]
    },
    {
      id: "generators-power",
      name: "مولدات وطاقة",
      subcategories: [
        "مولد كهربائي", "مولد ديزل", "مولد بنزين", "مولد محمول",
        "ضاغط هواء", "كمبروسر"
      ]
    },
    {
      id: "agricultural-equipment",
      name: "معدات زراعية",
      subcategories: [
        "جرار", "حصادة", "آلة حصاد العشب", "آلة بالات",
        "محراث", "مبذرة", "أنظمة الري", "مضخات زراعية"
      ]
    },
    {
      id: "industrial-machines",
      name: "معدات صناعية",
      children: [
        {
          id: "cnc-machines",
          name: "ماكينات CNC",
          subcategories: [
            "ماكينة CNC", "مخرطة CNC", "فريزة CNC", "مخرطة", "فريزة"
          ]
        },
        {
          id: "welding",
          name: "اللحام",
          subcategories: [
            "ماكينة لحام", "لحام أرجون", "لحام MIG/MAG"
          ]
        },
        {
          id: "cutting",
          name: "القص والتشكيل",
          subcategories: [
            "قص بلازما", "قص ليزر", "قص مياه", "منشار",
            "مكبس", "مكبس هيدروليكي", "ماكينة ثني", "ماكينة ثقب"
          ]
        }
      ]
    },
    {
      id: "packaging-printing",
      name: "التعبئة والطباعة",
      subcategories: [
        "ماكينة تعبئة", "ماكينة تغليف", "ماكينة طباعة", "طباعة رقمية"
      ]
    }
  ]
};

// الخدمات - Services
export const SERVICES_HIERARCHY: CategoryNode = {
  id: "hizmetler",
  name: "الخدمات",
  children: [
    {
      id: "home-services",
      name: "خدمات المنزل",
      subcategories: [
        "ترميم وتجديد", "دهان", "سباكة", "كهرباء",
        "تكييف وتبريد", "أعمال حدادة", "نجارة", "ألمنيوم",
        "تركيب مطابخ", "تركيب أثاث", "فك وتركيب"
      ]
    },
    {
      id: "cleaning-services",
      name: "التنظيف",
      subcategories: [
        "تنظيف منازل", "تنظيف سجاد", "تنظيف كنب", "تنظيف خزانات",
        "مكافحة حشرات", "تعقيم"
      ]
    },
    {
      id: "transport-services",
      name: "النقل",
      subcategories: [
        "نقل أثاث", "نقل عفش", "شحن بضائع", "توصيل طلبات"
      ]
    },
    {
      id: "car-services",
      name: "خدمات السيارات",
      subcategories: [
        "صيانة سيارات", "ميكانيكي", "كهربائي سيارات", "سمكرة ودهان",
        "غسيل سيارات", "تلميع سيارات", "تغيير زيت"
      ]
    },
    {
      id: "digital-services",
      name: "خدمات رقمية",
      subcategories: [
        "تصميم مواقع", "تطوير تطبيقات", "تصميم جرافيك", "طباعة", "تسويق رقمي",
        "إدارة سوشيال ميديا", "كتابة محتوى", "ترجمة"
      ]
    },
    {
      // Fashion design as a SERVICE (#173). «تصميم أزياء» under الدروس الخصوصية
      // stays the lesson; «تصميم جرافيك» stays under خدمات رقمية.
      id: "design-creative-services",
      name: "التصميم والإبداع",
      children: [
        {
          id: "fashion-design-services",
          name: "تصميم الأزياء",
          subcategories: [
            "تصميم أزياء نسائية", "تصميم أزياء رجالية", "تصميم أزياء أطفال",
            "تصميم أزياء مشتركة", "تصميم أزياء متنوعة"
          ]
        }
      ]
    },
    {
      id: "printing-customization-services",
      name: "الطباعة والتخصيص",
      subcategories: [
        "طباعة شعارات على الملابس", "طباعة على الأقمشة والمنسوجات"
      ]
    },
    {
      id: "photography-services",
      name: "تصوير",
      subcategories: [
        "تصوير فوتوغرافي", "تصوير فيديو", "تصوير منتجات", "تصوير مناسبات",
        "مونتاج فيديو"
      ]
    },
    {
      id: "events-services",
      name: "مناسبات",
      subcategories: [
        "تنظيم حفلات", "تنظيم أعراس", "ضيافة", "تأجير معدات",
        "دي جي", "فرقة موسيقية"
      ]
    },
    {
      id: "health-beauty-services",
      name: "صحة وجمال",
      subcategories: [
        "حلاق رجالي", "صالون نسائي", "مساج", "سبا"
      ]
    },
    {
      id: "legal-financial",
      name: "قانونية ومالية",
      subcategories: [
        "محاماة", "استشارات قانونية", "محاسبة", "استشارات مالية"
      ]
    },
    {
      id: "repair-services",
      name: "صيانة وتصليح",
      subcategories: [
        "صيانة أجهزة", "صيانة هواتف", "صيانة كمبيوتر"
      ]
    }
  ]
};

// الدروس الخصوصية - Private Lessons
export const LESSONS_HIERARCHY: CategoryNode = {
  id: "ozel-ders",
  name: "الدروس الخصوصية",
  children: [
    {
      id: "school-subjects",
      name: "المواد الدراسية",
      subcategories: [
        "رياضيات", "فيزياء", "كيمياء", "أحياء",
        "لغة عربية", "لغة إنجليزية", "لغة فرنسية", "لغة تركية",
        "تاريخ", "جغرافيا", "علوم", "اجتماعيات"
      ]
    },
    {
      id: "languages",
      name: "اللغات",
      subcategories: [
        "تعليم لغات", "محادثة إنجليزية", "IELTS", "TOEFL",
        "لغة ألمانية", "لغة إسبانية", "لغة صينية", "لغة يابانية"
      ]
    },
    {
      id: "music-lessons",
      name: "الموسيقى",
      subcategories: [
        "دروس موسيقى", "بيانو", "جيتار", "كمان", "عود",
        "درامز", "ناي", "غناء"
      ]
    },
    {
      id: "sports-lessons",
      name: "الرياضة",
      subcategories: [
        "سباحة", "تنس", "كرة قدم", "كرة سلة",
        "فنون قتالية", "كاراتيه", "تايكوندو", "جودو",
        "يوغا", "لياقة بدنية"
      ]
    },
    {
      id: "arts-lessons",
      name: "الفنون",
      subcategories: [
        "رسم وتلوين", "خط عربي", "تصوير فوتوغرافي",
        "تصميم جرافيك", "تصميم أزياء"
      ]
    },
    {
      id: "tech-lessons",
      name: "التقنية",
      subcategories: [
        "برمجة", "تطوير مواقع", "تصميم مواقع",
        "أوفيس", "فوتوشوب", "إليستريتور"
      ]
    },
    {
      id: "exam-prep",
      name: "تحضير امتحانات",
      subcategories: [
        "قدرات", "تحصيلي", "امتحانات دولية"
      ]
    }
  ]
};

// الوظائف - Jobs
export const JOBS_HIERARCHY: CategoryNode = {
  id: "is-ilanlari",
  name: "الوظائف",
  children: [
    {
      id: "it-jobs",
      name: "تقنية المعلومات",
      subcategories: [
        "مبرمج", "مطور ويب", "مطور تطبيقات", "مهندس IT",
        "مصمم جرافيك", "مصمم UI/UX", "محلل بيانات", "أمن معلومات"
      ]
    },
    {
      id: "sales-marketing-jobs",
      name: "المبيعات والتسويق",
      subcategories: [
        "مندوب مبيعات", "مسؤول تسويق", "تسويق رقمي", "إدارة سوشيال ميديا",
        "خدمة عملاء", "مسؤول علاقات عامة"
      ]
    },
    {
      id: "admin-jobs",
      name: "الإدارة",
      subcategories: [
        "مدير", "مدير مشاريع", "مساعد إداري", "سكرتارية",
        "موارد بشرية", "محاسب", "مدير مالي"
      ]
    },
    {
      id: "health-jobs",
      name: "الصحة",
      subcategories: [
        "طبيب", "ممرض", "صيدلي", "أخصائي علاج طبيعي",
        "طبيب أسنان", "مساعد طبي"
      ]
    },
    {
      id: "engineering-jobs",
      name: "الهندسة",
      subcategories: [
        "مهندس مدني", "مهندس معماري", "مهندس كهرباء", "مهندس ميكانيكا",
        "مهندس صناعي", "مشرف موقع"
      ]
    },
    {
      id: "education-jobs",
      name: "التعليم",
      subcategories: [
        "معلم", "مدرس لغة إنجليزية", "مدرس رياضيات", "مدرب"
      ]
    },
    {
      id: "hospitality-jobs",
      name: "الضيافة والسياحة",
      subcategories: [
        "شيف", "طباخ", "نادل", "موظف فندق",
        "مرشد سياحي", "موظف حجوزات"
      ]
    },
    {
      id: "other-jobs",
      name: "أخرى",
      subcategories: [
        "سائق", "عامل مستودع", "أمن وحراسة", "فني صيانة",
        "كاشير", "موظف استقبال", "عمل حر"
      ]
    }
  ]
};

// مطلوب مساعدين - Help Wanted
export const HELPERS_HIERARCHY: CategoryNode = {
  id: "yardimci-arayanlar",
  name: "مطلوب مساعدين",
  children: [
    {
      id: "caregiving",
      name: "رعاية",
      subcategories: [
        "مربية أطفال", "جليسة أطفال", "مربية منزلية",
        "رعاية مسنين", "ممرض منزلي", "مرافق"
      ]
    },
    {
      id: "home-help",
      name: "خدمة منزلية",
      subcategories: [
        "عاملة منزلية", "طباخة", "سائق خاص",
        "بستاني", "حارس منزل", "عامل نظافة"
      ]
    },
    {
      id: "office-shop-help",
      name: "مكتب ومتجر",
      subcategories: [
        "موظف متجر", "كاشير", "موظف استقبال",
        "مساعد مكتب", "ساعي"
      ]
    }
  ]
};

// الحيوانات - Pets & Animals
export const ANIMALS_HIERARCHY: CategoryNode = {
  id: "hayvanlar-alemi",
  name: "الحيوانات",
  children: ANIMAL_CATEGORY_GROUPS.map((group) => ({
    id: group.id,
    name: group.name,
    subcategories: [...group.subcategories],
  })),
};

export const CHILDREN_HIERARCHY: CategoryNode = {
  id: "atfal",
  name: "الأطفال",
  children: [
    {
      id: "kids-clothing",
      name: "ملابس الأطفال",
      children: [
        { id: "boys-clothing", name: "ملابس أولاد" },
        { id: "girls-clothing", name: "ملابس بنات" },
        { id: "infant-clothing", name: "ملابس رضع (0-2 سنة)" },
        { id: "newborn-clothing", name: "ملابس حديثي الولادة" },
        { id: "kids-pajamas", name: "بيجامات أطفال" },
        { id: "kids-underwear", name: "ملابس داخلية أطفال" },
        { id: "kids-swimwear", name: "ملابس سباحة أطفال" },
        { id: "costumes", name: "أزياء تنكرية" },
      ]
    },
    {
      id: "kids-shoes",
      name: "أحذية الأطفال",
      children: [
        { id: "boys-shoes", name: "أحذية أولاد" },
        { id: "girls-shoes", name: "أحذية بنات" },
        { id: "kids-sneakers", name: "أحذية رياضية أطفال" },
        { id: "kids-sandals", name: "صنادل أطفال" },
        { id: "infant-shoes", name: "أحذية رضع" },
      ]
    },
    {
      id: "baby-essentials",
      name: "مستلزمات الرضع",
      children: [
        { id: "stroller", name: "عربة أطفال" },
        { id: "car-seat", name: "كرسي سيارة أطفال" },
        { id: "kids-bed", name: "سرير أطفال" },
        { id: "baby-crib", name: "سرير رضع" },
        { id: "high-chair", name: "كرسي طعام أطفال" },
        { id: "baby-carrier", name: "حاملة أطفال (كاريير)" },
        { id: "baby-walker", name: "مشاية أطفال" },
        { id: "safety-gate", name: "حاجز أمان" },
      ]
    },
    {
      id: "feeding-nutrition",
      name: "الرضاعة والتغذية",
      children: [
        { id: "baby-bottles", name: "رضاعات" },
        { id: "baby-formula", name: "حليب أطفال" },
        { id: "baby-food", name: "أغذية أطفال" },
        { id: "baby-food-blender", name: "خلاط طعام أطفال" },
        { id: "baby-dishes", name: "أطباق وملاعق أطفال" },
        { id: "nursing-chair", name: "كرسي رضاعة" },
        { id: "nursing-pillow", name: "وسادة رضاعة" },
        { id: "breast-pump", name: "مضخة حليب" },
      ]
    },
    {
      id: "diapers-hygiene",
      name: "الحفاضات والنظافة",
      children: [
        { id: "diapers", name: "حفاضات" },
        { id: "wet-wipes", name: "مناديل مبللة" },
        { id: "diaper-bag", name: "حقيبة حفاضات" },
        { id: "baby-bath", name: "بانيو أطفال" },
        { id: "baby-bath-supplies", name: "مستلزمات استحمام أطفال" },
        { id: "baby-skincare", name: "كريمات وعناية بالبشرة" },
        { id: "baby-shampoo", name: "شامبو أطفال" },
      ]
    },
    {
      id: "kids-toys",
      name: "الألعاب",
      children: [
        { id: "educational-toys", name: "ألعاب تعليمية" },
        { id: "infant-toys", name: "ألعاب أطفال رضع" },
        { id: "dolls-figures", name: "دمى ومجسمات" },
        { id: "building-toys", name: "ألعاب تركيب (ليغو)" },
        { id: "kids-electronic-toys", name: "ألعاب إلكترونية أطفال" },
        { id: "outdoor-toys", name: "ألعاب خارجية" },
        { id: "swings-slides", name: "مراجيح وزحاليق" },
        { id: "kids-pools", name: "مسابح أطفال" },
        { id: "kids-ride-on", name: "ألعاب سيارات ودراجات أطفال" },
        { id: "kids-bikes", name: "دراجات أطفال" },
      ]
    },
    {
      id: "school-education",
      name: "المدرسة والتعليم",
      children: [
        { id: "school-bags", name: "حقائب مدرسية" },
        { id: "stationery", name: "قرطاسية" },
        { id: "school-supplies", name: "أدوات مدرسية" },
        { id: "kids-books", name: "كتب أطفال" },
        { id: "kids-stories", name: "قصص أطفال" },
      ]
    },
    {
      id: "kids-furniture",
      name: "أثاث الأطفال",
      children: [
        { id: "kids-bedroom", name: "غرفة نوم أطفال" },
        { id: "kids-wardrobe", name: "خزانة ملابس أطفال" },
        { id: "kids-study-desk", name: "مكتب دراسة أطفال" },
        { id: "kids-shelves", name: "رفوف أطفال" },
      ]
    },
    {
      id: "kids-health-safety",
      name: "صحة وأمان",
      children: [
        { id: "baby-thermometer", name: "ميزان حرارة أطفال" },
        { id: "baby-monitor", name: "جهاز مراقبة أطفال (بيبي مونيتور)" },
        { id: "baby-medicines", name: "أدوية ومكملات أطفال" },
        { id: "bottle-sterilizers", name: "معقمات رضاعات" },
        { id: "teething-supplies", name: "مستلزمات التسنين" },
      ]
    },
    {
      id: "kids-other",
      name: "أخرى",
      children: [
        { id: "kids-gifts", name: "هدايا أطفال" },
        { id: "kids-room-decor", name: "ديكور غرف أطفال" },
        { id: "kids-misc", name: "أخرى" },
      ]
    }
  ]
};

export const MEDICAL_HIERARCHY: CategoryNode = {
  id: "tibbi",
  name: "المستلزمات الطبية",
  children: [
    {
      id: "medical-devices",
      name: "الأجهزة الطبية",
      children: [
        { id: "blood-pressure-devices", name: "أجهزة قياس ضغط الدم" },
        { id: "glucose-meters", name: "أجهزة قياس السكر" },
        { id: "pulse-oximeters", name: "أجهزة قياس الأكسجين" },
        { id: "ecg-devices", name: "أجهزة تخطيط القلب" },
        { id: "ultrasound-devices", name: "أجهزة الموجات فوق الصوتية" },
        { id: "sterilization-devices", name: "أجهزة التعقيم" },
      ]
    },
    {
      id: "hospital-supplies",
      name: "مستلزمات المستشفيات والعيادات",
      children: [
        { id: "hospital-beds", name: "أسرة مستشفيات" },
        { id: "patient-stretchers", name: "نقالات مرضى" },
        { id: "medical-carts", name: "عربات طبية" },
        { id: "medical-curtains", name: "ستائر طبية" },
        { id: "medical-waste-containers", name: "حاويات نفايات طبية" },
        { id: "disposable-covers", name: "أغطية يد واحدة" },
      ]
    },
    {
      id: "consumables",
      name: "المواد الاستهلاكية",
      children: [
        { id: "gauze-bandages", name: "شاش وضمادات" },
        { id: "medical-cotton", name: "قطن طبي" },
        { id: "medical-tape", name: "أشرطة لاصقة طبية" },
        { id: "medical-gloves", name: "قفازات طبية" },
        { id: "masks", name: "كمامات" },
        { id: "syringes", name: "حقن (بدون أدوية)" },
      ]
    },
    {
      id: "laboratory",
      name: "معدات المختبرات",
      children: [
        { id: "lab-analyzers", name: "أجهزة تحليل مخبرية" },
        { id: "microscopes", name: "مجاهر" },
        { id: "test-tubes", name: "أنابيب اختبار" },
        { id: "sample-containers", name: "حاويات عينات" },
        { id: "blood-draw-supplies", name: "مستلزمات سحب الدم" },
      ]
    },
    {
      id: "rehabilitation",
      name: "التأهيل والحركة",
      children: [
        { id: "wheelchairs", name: "كراسي متحركة" },
        { id: "walkers-crutches", name: "مشايات وعكازات" },
        { id: "prosthetics", name: "أطراف صناعية" },
        { id: "splints-braces", name: "جبائر ودعامات" },
        { id: "physiotherapy-devices", name: "أجهزة علاج طبيعي" },
      ]
    },
    {
      id: "home-care",
      name: "الرعاية المنزلية",
      children: [
        { id: "home-care-beds", name: "أسرة رعاية منزلية" },
        { id: "medical-mattresses", name: "فرشات طبية" },
        { id: "oxygen-concentrators", name: "مكثفات أكسجين" },
        { id: "suction-devices", name: "أجهزة شفط" },
        { id: "patient-monitors", name: "أجهزة مراقبة المريض" },
      ]
    },
    {
      id: "nursing-supplies",
      name: "مستلزمات التمريض",
      children: [
        { id: "catheters", name: "قسطرات" },
        { id: "feeding-tubes", name: "أنابيب تغذية" },
        { id: "urine-bags", name: "أكياس بول" },
        { id: "vital-signs-monitors", name: "أجهزة قياس العلامات الحيوية" },
        { id: "iv-accessories", name: "ملحقات المحاليل" },
      ]
    },
    {
      id: "dental",
      name: "مستلزمات طب الأسنان",
      children: [
        { id: "dental-chairs", name: "كراسي أسنان" },
        { id: "dental-tools", name: "أدوات طب الأسنان" },
        { id: "dental-sterilizers", name: "أجهزة تعقيم الأسنان" },
        { id: "orthodontic-tools", name: "أدوات تقويم الأسنان" },
      ]
    },
    {
      id: "ophthalmology",
      name: "طب العيون والبصريات",
      children: [
        { id: "prescription-glasses", name: "نظارات طبية" },
        { id: "contact-lenses", name: "عدسات لاصقة" },
        { id: "vision-test-devices", name: "أجهزة فحص النظر" },
        { id: "ophthalmology-tools", name: "أدوات طب العيون" },
      ]
    },
    {
      id: "surgical",
      name: "الأدوات الجراحية",
      children: [
        { id: "surgical-tools", name: "أدوات جراحية" },
        { id: "sutures", name: "خيوط جراحية" },
        { id: "electrosurgical-devices", name: "أجهزة كي كهربائي" },
        { id: "or-equipment", name: "معدات غرف العمليات" },
      ]
    },
    {
      id: "infection-control",
      name: "مكافحة العدوى والسلامة",
      children: [
        { id: "ppe", name: "معدات الحماية الشخصية (PPE)" },
        { id: "sterilization-supplies", name: "ملحقات التعقيم" },
        { id: "protective-clothing", name: "ملابس واقية" },
        { id: "medical-waste-safety", name: "أدوات سلامة النفايات الطبية" },
      ]
    }
  ]
};

// Map category keys to their hierarchies
export const CATEGORY_HIERARCHIES: { [key: string]: CategoryNode } = {
  "emlak": REAL_ESTATE_HIERARCHY,
  "vasita": VEHICLES_HIERARCHY,
  "yedek-parca": SPARE_PARTS_HIERARCHY,
  "alisveris": SHOPPING_HIERARCHY,
  "aghziya": FOOD_HIERARCHY,
  "riyada": SPORTS_HIERARCHY,
  "atfal": CHILDREN_HIERARCHY,
  "tibbi": MEDICAL_HIERARCHY,
  "is-makineleri": INDUSTRIAL_HIERARCHY,
  "hizmetler": SERVICES_HIERARCHY,
  "ozel-ders": LESSONS_HIERARCHY,
  "is-ilanlari": JOBS_HIERARCHY,
  "yardimci-arayanlar": HELPERS_HIERARCHY,
  "hayvanlar-alemi": ANIMALS_HIERARCHY,
};

// Helper function to find a node by ID in the hierarchy
export function findNodeById(node: CategoryNode, id: string): CategoryNode | null {
  if (node.id === id) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
}

// Helper function to get breadcrumb path to a node
export function getBreadcrumbPath(node: CategoryNode, targetId: string, path: CategoryNode[] = []): CategoryNode[] | null {
  if (node.id === targetId) {
    return [...path, node];
  }
  if (node.children) {
    for (const child of node.children) {
      const result = getBreadcrumbPath(child, targetId, [...path, node]);
      if (result) return result;
    }
  }
  return null;
}

// Helper function to get all subcategories under a node (for filtering listings)
export function getAllSubcategories(node: CategoryNode): string[] {
  const subcategories: string[] = [];
  if (node.subcategories) {
    subcategories.push(...node.subcategories);
  }
  if (node.children) {
    for (const child of node.children) {
      subcategories.push(...getAllSubcategories(child));
    }
  }
  if (!node.children && !node.subcategories) {
    subcategories.push(node.name);
  }
  return subcategories;
}
